import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  KeyRound,
  ShieldCheck,
  AlertTriangle,
  Copy,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import { isAxiosError } from 'axios'
import {
  getKeyConfig,
  setKeyConfig,
  validateCustomerKey,
  getKeyHealth,
  getKeyPolicyTemplate,
  rewrapDeks,
  offboardKeys,
  revokeOrgKeys,
} from '../../services/api'
import PurgeStateNotice from '../../components/encryption/PurgeStateNotice'
import { withStepUp } from '../../components/encryption/withStepUp'
import type { KeyValidation } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import PageHeader from '../../components/ui/PageHeader'
import ContextualTip from '../../components/ui/ContextualTip'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'

type CustodyMode = 'platform' | 'byok'

const REQUIRED_REGION = 'ap-southeast-2'

// KMS ARN shape: arn:aws:kms:<region>:<account>:key/<key-id>
const KMS_ARN_RE = /^arn:aws:kms:[a-z0-9-]+:\d{12}:key\/[A-Za-z0-9-]+$/

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errorMessage(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined
    return data?.error || data?.message || err.message || fallback
  }
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Map the backend validation `reason` code to a clear, actionable message.
 * The mockup calls out residency_violation / grant_missing /
 * key_unavailable / key_disabled explicitly.
 */
function reasonMessage(reason: string | undefined, detail?: string): string {
  switch (reason) {
    case 'residency_violation':
      return `Key must be in ${REQUIRED_REGION}. Australian data residency requires the KMS key to live in the Sydney region.`
    case 'grant_missing':
      return 'Our service role is not yet granted on the key. Apply the key-policy grant below (or deploy the CloudFormation template), then validate again.'
    case 'key_unavailable':
      return 'The key could not be reached. Check the ARN is correct and the key exists in your account.'
    case 'key_disabled':
      return 'The key is disabled. Re-enable it in AWS KMS before using it for encryption.'
    default:
      return detail || reason || 'The key could not be validated. Check the ARN and key policy.'
  }
}

function regionFromArn(arn: string): string | null {
  const parts = arn.split(':')
  return parts.length >= 4 ? parts[3] || null : null
}

function prettyJson(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrgEncryptionKeys() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const orgId = user?.active_org_id || user?.org_memberships?.[0]?.org_id
  const activeMembership =
    user?.org_memberships?.find((m) => m.org_id === orgId) || user?.org_memberships?.[0]
  const orgName = activeMembership?.org_name
  const isOrgAdmin =
    activeMembership?.role === 'admin' ||
    activeMembership?.role === 'owner' ||
    (user?.groups?.includes('innovateflow-admin') ?? false)

  // ---- Queries (hooks must run unconditionally) -------------------------
  const {
    data: config,
    isLoading: configLoading,
    isError: configError,
  } = useQuery({
    queryKey: ['org-key-config', orgId],
    queryFn: () => getKeyConfig(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  const {
    data: health,
    isFetching: healthFetching,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['org-key-health', orgId],
    queryFn: () => getKeyHealth(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  const { data: policy } = useQuery({
    queryKey: ['org-key-policy', orgId],
    queryFn: () => getKeyPolicyTemplate(orgId as string),
    enabled: !!orgId && isOrgAdmin,
  })

  // ---- Local UI state ----------------------------------------------------
  // The mode the admin is *editing* toward (separate from the persisted config).
  const [draftMode, setDraftMode] = useState<CustodyMode | null>(null)
  const [arnInput, setArnInput] = useState<string>('')
  const [validation, setValidation] = useState<KeyValidation | null>(null)
  const [cutoverOpen, setCutoverOpen] = useState(false)
  const [offboardOpen, setOffboardOpen] = useState(false)
  const [revokeOpen, setRevokeOpen] = useState(false)
  const [switchToPlatformOpen, setSwitchToPlatformOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  // BUG-149: seed the ARN input from the persisted config exactly once. After
  // that, `arnInput` is the single source of truth so the field can be cleared
  // or freely edited (previously the value snapped back to the persisted ARN
  // because the input fell back to `config?.customer_kms_arn` whenever empty).
  const arnSeeded = useRef(false)
  useEffect(() => {
    if (!arnSeeded.current && config?.customer_kms_arn) {
      setArnInput(config.customer_kms_arn)
      arnSeeded.current = true
    }
  }, [config?.customer_kms_arn])

  // ---- Mutations ---------------------------------------------------------
  const validateMutation = useMutation({
    mutationFn: (arn: string) => validateCustomerKey(orgId as string, arn),
    onSuccess: (result) => setValidation(result),
    onError: (err) => {
      setValidation(null)
      setActionError(errorMessage(err, 'Validation request failed. Please try again.'))
    },
  })

  const cutoverMutation = useMutation({
    // Persist BYOK config, then re-wrap the DEKs under the customer key.
    // BUG-117: if the re-wrap fails AFTER the config was already flipped to
    // BYOK, roll the config back to the prior custody mode so we never leave
    // custody=byok while the DEKs are still wrapped under the old key.
    mutationFn: async (arn: string) => {
      const previousMode = config?.custody_mode ?? 'platform'
      const previousArn = config?.customer_kms_arn ?? undefined
      await setKeyConfig(orgId as string, { custody_mode: 'byok', customer_kms_arn: arn })
      try {
        return await rewrapDeks(orgId as string)
      } catch (rewrapErr) {
        // Best-effort rollback; the failure is re-thrown regardless so onError
        // surfaces it and refetches the true persisted state.
        try {
          await setKeyConfig(orgId as string, {
            custody_mode: previousMode,
            ...(previousMode === 'byok' && previousArn ? { customer_kms_arn: previousArn } : {}),
          })
        } catch {
          /* rollback itself failed — onError still invalidates + warns */
        }
        throw rewrapErr
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
      setCutoverOpen(false)
      setDraftMode(null)
    },
    onError: () => {
      // BUG-117: the config may have been flipped and then rolled back — refetch
      // so the UI reflects the real persisted custody mode rather than a stale
      // "already switched" view. The ConfirmDialog surfaces the error inline
      // (onConfirm re-throws), so we deliberately do NOT close it here.
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
    },
  })

  // BUG-116: an explicit, persisted switch BACK to platform-managed custody.
  // A BYOK org must be able to hand custody back to SecureEmbed — this writes
  // custody_mode:'platform' and re-wraps the DEKs under the platform key.
  const switchToPlatformMutation = useMutation({
    mutationFn: async () => {
      await setKeyConfig(orgId as string, { custody_mode: 'platform' })
      return rewrapDeks(orgId as string)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
      setSwitchToPlatformOpen(false)
      setDraftMode(null)
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
    },
  })

  // Story #6490 2.6 / 3.6: on a tier whose row carries requires_webauthn the API
  // answers 403 {requires_webauthn: true}; withStepUp runs the per-target
  // ceremony (org-offboard:<org> / org-revoke:<org>) and retries ONCE. The page
  // renders the outcome from GET /org-keys/{org} (config.purge_state), never
  // from a mutation response — a 504 on the revoke simply re-reads the row.
  const offboardMutation = useMutation({
    mutationFn: () => withStepUp((t) => offboardKeys(orgId as string, t), `org-offboard:${orgId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
      setOffboardOpen(false)
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: () => withStepUp((t) => revokeOrgKeys(orgId as string, t), `org-revoke:${orgId}`),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['org-key-config', orgId] })
      queryClient.invalidateQueries({ queryKey: ['org-key-health', orgId] })
    },
    onSuccess: () => setRevokeOpen(false),
  })

  // ---- Access gate -------------------------------------------------------
  if (!isOrgAdmin || !orgId) {
    return (
      <div className="flex items-center justify-center py-24 px-4">
        <div className="light-card p-8 max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={26} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">
            Organisation admin access required
          </h2>
          <p className="text-sm text-gray-500">
            You need to be an owner or admin of this organisation to manage encryption keys.
          </p>
        </div>
      </div>
    )
  }

  // ---- Derived -----------------------------------------------------------
  const persistedMode: CustodyMode = config?.custody_mode ?? 'platform'
  // The mode currently selected in the UI (draft overrides persisted).
  const selectedMode: CustodyMode = draftMode ?? persistedMode
  const isByokView = selectedMode === 'byok'

  // BUG-149: the ARN input is the single source of truth (seeded once above),
  // so it can be cleared without snapping back to the persisted value.
  const effectiveArn = arnInput.trim()
  const arnFormatValid = KMS_ARN_RE.test(effectiveArn)
  const arnRegion = effectiveArn ? regionFromArn(effectiveArn) : null
  const arnRegionOk = arnRegion === REQUIRED_REGION
  const validationPassed = validation?.valid === true

  // Ready to cut over only when the persisted config isn't already BYOK on this
  // exact ARN, the format is valid, the region is correct and validation passed.
  const alreadyByokSameKey =
    persistedMode === 'byok' && config?.customer_kms_arn === effectiveArn
  const canCutOver =
    arnFormatValid && arnRegionOk && validationPassed && !alreadyByokSameKey

  // The /org-keys/{org}/health endpoint returns { status: 'healthy' } for a
  // platform-managed org and has no `healthy`/`valid`/`key_state` fields — so the
  // old `health?.healthy ?? health?.valid` was always undefined → "Degraded" +
  // "KEY STATE —" even on a perfectly healthy platform key. Accept the real shape.
  // Story #6490 8.1 — the organisation's deletion record, from GET /org-keys/{org}.
  // in_progress / owed: a deletion is running, so Revoke and Offboard wait for it
  // (the API's purge claim would refuse a second one). nothing_attributed renders
  // amber and exhausted red via PurgeStateNotice; exhausted says support "has been
  // alerted" only once the row carries purge_alert_sent_at.
  const purgeRunning = config?.purge_state === 'in_progress' || config?.purge_state === 'owed'

  const healthHealthy =
    health?.status === 'healthy' || health?.healthy === true || health?.valid === true

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organisation Encryption Keys"
        description={`Choose how your organisation's data encryption keys are managed. All data is encrypted at rest either way — this controls who holds the root key.${
          orgName ? ` · ${orgName}` : ''
        }`}
        helpKey="org-keys"
        status={{
          label: persistedMode === 'byok' ? 'Your own key (BYOK)' : 'Platform-managed',
          tone: persistedMode === 'byok' ? 'info' : 'neutral',
        }}
      />

      {/* Load error banner */}
      {configError && (
        <div className="light-card p-4 flex items-center gap-3 border border-red-200 bg-red-50">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">
            Failed to load key configuration. Please refresh the page or try again shortly.
          </p>
        </div>
      )}

      {/* Action error banner */}
      {actionError && (
        <div className="light-card p-4 flex items-start gap-3 border border-red-200 bg-red-50">
          <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {configLoading ? (
        <LoadingSkeleton rows={2} type="card" />
      ) : (
        <>
          <ContextualTip id="org-keys-byok">
            <b>BYOK</b> (bring your own key) lets your organisation hold the root encryption key in
            your own AWS KMS — you control the key policy and can revoke our access instantly, an
            in-your-hands kill switch. Platform-managed is simpler and needs zero setup; either way
            your documents are always encrypted at rest.
          </ContextualTip>

          {/* ---- Section 1: Custody mode selector ------------------------ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CustodyModeCard
              title="Platform-managed"
              badge="Default · all tiers"
              badgeClass="bg-gray-500/20 text-gray-600"
              description="A dedicated AWS KMS key for your organisation, created and rotated by SecureEmbed in ap-southeast-2. Zero setup."
              selected={selectedMode === 'platform'}
              onSelect={() => {
                setDraftMode('platform')
                setValidation(null)
                setActionError(null)
              }}
            />
            <CustodyModeCard
              title="Organisation-managed (BYOK)"
              badge="Enterprise"
              badgeClass="bg-violet-500/20 text-violet-700"
              description="Bring your own AWS KMS key. You control the key policy and can revoke our access at any time — an instant kill switch."
              selected={selectedMode === 'byok'}
              onSelect={() => {
                setDraftMode('byok')
                setActionError(null)
              }}
            />
          </div>

          {/* ---- Section 2: Platform-managed panel (read-only) ----------- */}
          {!isByokView && (
            <div className="light-card p-5 lg:p-6">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={18} className="text-iflow-600" />
                <h2 className="text-base font-bold text-gray-800">Platform-managed key</h2>
                {/* BUG-116: only claim "Active" when custody is ACTUALLY platform.
                    While the org is still on BYOK, show an honest pending state
                    instead of a fake green badge. */}
                {persistedMode === 'platform' ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-700">
                    <CheckCircle size={12} /> {config?.status || 'Active'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-700">
                    <AlertTriangle size={12} /> Currently on your own key (BYOK)
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-4 max-w-2xl">
                SecureEmbed encrypts your organisation's data at rest under its platform-managed KMS
                keys in {REQUIRED_REGION}. Nothing to configure.
              </p>
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* BUG: org-keys scoping — the backend's GET /org-keys/{orgId} response
                    (org_keys/handler.py:_default_config) never returns a per-org KMS alias;
                    platform mode is a DynamoDB custody marker only, no dedicated org key is
                    ever provisioned (kms.create_key is never called for it). The previous
                    code fabricated `alias/secureembed-${orgId}` client-side — for a caller
                    whose org membership falls back to the auto-created personal pseudo-org
                    (org_id `personal-<user_id>`, see shared/auth.py:_create_personal_org)
                    that literally rendered as "alias/secureembed-personal-<uuid>", mislabelling
                    a fallback identifier as if it were a real per-org KMS key. Show the honest
                    state instead of inventing an alias the backend never returned. */}
                <ReadOnlyField
                  label="Key alias"
                  value="Not provisioned — organisation uses SecureEmbed's shared platform-default encryption key"
                />
                <ReadOnlyField label="Rotation" value={config?.rotation || 'Annual (automatic)'} />
                <ReadOnlyField label="Region" value={REQUIRED_REGION} />
              </dl>
              {/* BUG-116: BYOK org can hand custody back — a real, persisted action. */}
              {persistedMode === 'byok' && (
                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-iflow-100 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setActionError(null)
                      setSwitchToPlatformOpen(true)
                    }}
                    disabled={switchToPlatformMutation.isPending}
                    className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw size={16} />
                    {switchToPlatformMutation.isPending
                      ? 'Switching…'
                      : 'Switch to platform-managed & re-wrap'}
                  </button>
                  <span className="text-xs text-gray-400 max-w-md">
                    Your data is currently under your own KMS key. This returns custody to the
                    SecureEmbed-managed key and re-wraps your data keys under it — the data itself is
                    not re-encrypted.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ---- Section 3: BYOK panel — ARN + validate ------------------ */}
          {isByokView && (
            <div className="light-card p-5 lg:p-6 space-y-4">
              <div className="flex items-center gap-2">
                <KeyRound size={18} className="text-iflow-600" />
                <h2 className="text-base font-bold text-gray-800">Your customer KMS key</h2>
              </div>

              <div>
                <label
                  htmlFor="byok-arn"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Customer KMS key ARN (must be in {REQUIRED_REGION})
                </label>
                <input
                  id="byok-arn"
                  type="text"
                  value={arnInput}
                  onChange={(e) => {
                    setArnInput(e.target.value)
                    setValidation(null)
                  }}
                  placeholder={`arn:aws:kms:${REQUIRED_REGION}:123456789012:key/abcd1234-...`}
                  spellCheck={false}
                  className="w-full border border-iflow-200 rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-iflow-500"
                />
                {effectiveArn && !arnFormatValid && (
                  <p className="text-xs text-red-600 mt-1">
                    That doesn't look like a valid KMS key ARN.
                  </p>
                )}
                {arnFormatValid && !arnRegionOk && (
                  <p className="text-xs text-amber-700 mt-1">
                    Key must be in {REQUIRED_REGION} (detected{' '}
                    <span className="font-mono">{arnRegion || 'unknown'}</span>) — Australian data
                    residency.
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null)
                    if (arnFormatValid) validateMutation.mutate(effectiveArn)
                  }}
                  disabled={!arnFormatValid || validateMutation.isPending}
                  className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ShieldCheck size={16} />
                  {validateMutation.isPending ? 'Validating…' : 'Validate key'}
                </button>
                {validation && (
                  <span className="text-xs text-gray-400">
                    Checked{' '}
                    {validation.checked_at
                      ? new Date(validation.checked_at).toLocaleString()
                      : 'just now'}
                  </span>
                )}
              </div>

              {/* Validation result */}
              {validation && (
                <div
                  className={`rounded-lg border px-4 py-3 ${
                    validation.valid
                      ? 'border-green-200 bg-green-50'
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {validation.valid ? (
                      <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <p
                        className={`text-sm font-semibold ${
                          validation.valid ? 'text-green-800' : 'text-red-800'
                        }`}
                      >
                        {validation.valid
                          ? 'Key validated — Encrypt / Decrypt / GenerateDataKey all succeeded.'
                          : reasonMessage(validation.reason, validation.detail)}
                      </p>
                      {validation.valid && validation.detail && (
                        <p className="text-xs text-green-700 mt-1">{validation.detail}</p>
                      )}
                      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                        <ReadOnlyField
                          label="Key state"
                          value={validation.key_state || '—'}
                          compact
                        />
                        <ReadOnlyField
                          label="Region"
                          value={validation.region || arnRegion || '—'}
                          compact
                        />
                        <ReadOnlyField
                          label="Rotation"
                          value={
                            validation.rotation_enabled == null
                              ? '—'
                              : validation.rotation_enabled
                                ? 'Enabled'
                                : 'Disabled'
                          }
                          compact
                        />
                      </dl>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- Section 4: Key-policy + CloudFormation ------------------ */}
          {isByokView && (
            <div className="light-card p-5 lg:p-6 space-y-4">
              <div>
                <h2 className="text-base font-bold text-gray-800">Grant our service role</h2>
                <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                  Add this statement to your key policy (or deploy the CloudFormation template). It
                  scopes our access to your organisation via an encryption-context condition.
                </p>
              </div>

              <KeyPolicyBlock statement={policy?.statement} />

              <div className="flex flex-wrap items-center gap-3">
                <CopyButton
                  label="Copy key policy"
                  text={prettyJson(policy?.statement)}
                  disabled={!policy?.statement}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!policy?.cloudformation) return
                    const blob = new Blob([policy.cloudformation], {
                      type: 'application/x-yaml',
                    })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `securembed-byok-${orgId}.yaml`
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(url)
                  }}
                  disabled={!policy?.cloudformation}
                  className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download size={16} /> Download CloudFormation
                </button>
              </div>

              {policy?.encryption_context != null && (
                <div className="rounded-lg bg-iflow-50/60 border border-iflow-100 px-4 py-3">
                  <p className="text-xs font-semibold text-iflow-900 mb-1">
                    Encryption context (enforced on every call)
                  </p>
                  <pre className="text-xs font-mono text-iflow-900/80 whitespace-pre-wrap break-all">
                    {prettyJson(policy.encryption_context)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* ---- Section 5: Cut-over card -------------------------------- */}
          {isByokView && (
            <div className="light-card p-5 lg:p-6">
              <h2 className="text-base font-bold text-gray-800">
                {persistedMode === 'byok' ? 'Re-wrap data keys' : 'Switch to BYOK'}
              </h2>
              <div className="rounded-lg bg-iflow-50/60 border border-iflow-100 px-4 py-3 mt-3">
                <p className="text-sm text-iflow-900">
                  <span className="font-semibold">How cut-over works:</span> your documents and
                  records are encrypted with per-agreement data keys (DEKs). Switching re-wraps
                  those DEKs under your key — the data itself is never bulk re-encrypted, so cut-over
                  takes minutes, not days.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null)
                    setCutoverOpen(true)
                  }}
                  disabled={!canCutOver || cutoverMutation.isPending}
                  className="inline-flex items-center gap-2 bg-iflow-700 hover:bg-iflow-800 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={16} />
                  {cutoverMutation.isPending
                    ? 'Re-wrapping…'
                    : persistedMode === 'byok'
                      ? 'Re-wrap DEKs'
                      : 'Switch to BYOK & re-wrap'}
                </button>
                {!validationPassed && (
                  <span className="text-xs text-gray-400">
                    Validate the key above before cutting over.
                  </span>
                )}
                {validationPassed && alreadyByokSameKey && (
                  <span className="text-xs text-gray-400">
                    Already live on this key — nothing to re-wrap.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ---- Section 6: Health / rotation panel --------------------- */}
          <div className="light-card p-5 lg:p-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-800">Key health &amp; rotation</h2>
                {health && (
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                      healthHealthy
                        ? 'bg-green-500/20 text-green-700'
                        : 'bg-red-500/20 text-red-700'
                    }`}
                  >
                    {healthHealthy ? <CheckCircle size={12} /> : <XCircle size={12} />}
                    {healthHealthy ? 'Healthy' : 'Degraded'}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setActionError(null)
                  void refetchHealth()
                }}
                disabled={healthFetching}
                className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-iflow-50 disabled:opacity-50"
              >
                <RefreshCw size={14} className={healthFetching ? 'animate-spin' : ''} /> Re-check
              </button>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <ReadOnlyField label="Key state" value={health?.key_state || (healthHealthy ? 'Enabled' : '—')} />
              <ReadOnlyField
                label="Rotation"
                value={
                  health?.rotation_enabled == null
                    ? config?.rotation || '—'
                    : health.rotation_enabled
                      ? 'Enabled'
                      : 'Disabled'
                }
              />
              <ReadOnlyField
                label="Last verified"
                value={
                  health?.checked_at
                    ? new Date(health.checked_at).toLocaleString()
                    : healthFetching
                      ? 'Checking…'
                      : '—'
                }
              />
            </dl>
            <p className="text-xs text-gray-400 mt-3">
              Grant is re-checked every 5 minutes and on every decrypt.
            </p>
          </div>

          {/* ---- Section 7: Kill-switch warning ------------------------- */}
          <div className="light-card p-5 lg:p-6 border border-amber-200 bg-amber-50">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-700 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h2 className="text-base font-bold text-amber-900">Kill switch</h2>
                {/* Story #6490 8.1 — verbatim plan copy. Bug #6482 owns every other sentence on this page. */}
                <p className="text-sm text-amber-800 mt-1" data-testid="kill-switch-copy">
                  Disabling the key or removing the grant makes documents and fields encrypted
                  under it unreadable within about five minutes, including to SecureEmbed staff,
                  and pauses search and analysis for your organisation. Disabling the key also
                  deletes the search entries and derived vectors for projects attributed to this
                  organisation within about ten minutes; removing the grant pauses them. Revoke
                  deletes them at once; Offboard opens the export grace window, and the deletion
                  happens at its revocation step — a large organisation&apos;s deletion can continue
                  for about six minutes, or up to about fifteen if the deletion was interrupted,
                  and shows as in progress until it is verified complete. Backups holding earlier
                  copies age out within 14 days.
                </p>
                {config?.purge_state && (
                  <div className="mt-3">
                    <PurgeStateNotice testId="org-purge-state" state={config.purge_state}
                                      alertSentAt={config.purge_alert_sent_at} />
                  </div>
                )}
                <p className="text-sm text-amber-800 mt-2">
                  A revocation <b>without prior offboarding</b> triggers a <b>7-day grace banner</b>{' '}
                  shown to your admins before cached DEKs are purged — after that, recovery is
                  impossible.
                </p>
              </div>
            </div>
          </div>

          {/* ---- Section 8: Offboarding / crypto-shred ------------------ */}
          <div className="light-card p-5 lg:p-6">
            <h2 className="text-base font-bold text-gray-800">Offboard &amp; crypto-shred</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-2xl">
              Cleanly retire this key. We run an ordered sequence:{' '}
              <b>export → retention-check → revocation</b>. Data you are entitled to keep is exported
              first, retention obligations are verified, then the wrapping key is revoked and cached
              DEKs are crypto-shredded. This is irreversible.
            </p>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => {
                  setActionError(null)
                  setOffboardOpen(true)
                }}
                disabled={offboardMutation.isPending || purgeRunning}
                className="inline-flex items-center gap-2 bg-white text-red-700 border border-red-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AlertTriangle size={16} />
                {offboardMutation.isPending ? 'Offboarding…' : 'Offboard / crypto-shred'}
              </button>
              {persistedMode === 'byok' && (
                <button
                  type="button"
                  data-testid="org-revoke"
                  onClick={() => {
                    setActionError(null)
                    setRevokeOpen(true)
                  }}
                  disabled={revokeMutation.isPending || purgeRunning}
                  className="ml-3 inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle size={16} />
                  {revokeMutation.isPending ? 'Revoking…' : 'Revoke platform access'}
                </button>
              )}
            </div>
          </div>

          {/* ---- Section 9: Key event audit trail ----------------------- */}
          <div className="light-card p-0 overflow-hidden">
            <div className="px-5 lg:px-6 pt-5">
              <h2 className="text-base font-bold text-gray-800">Key events</h2>
              <p className="text-sm text-gray-500 mt-1">
                An audit trail of every validation, cut-over, grant check and revocation.
              </p>
            </div>
            <div className="overflow-x-auto mt-3">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-iflow-100">
                    <th className="px-5 lg:px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 w-48">
                      When
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Event
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={2} className="px-5 lg:px-6 py-10 text-center">
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <KeyRound size={26} />
                        <p className="text-sm font-medium text-gray-500">No key events yet</p>
                        <p className="text-xs text-gray-400 max-w-sm">
                          Key events — validations, cut-overs, grant checks and revocations — will
                          appear here as they happen.
                        </p>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ---- Confirm dialogs ---------------------------------------------- */}
      <ConfirmDialog
        open={cutoverOpen}
        title={persistedMode === 'byok' ? 'Re-wrap data keys?' : 'Switch to BYOK?'}
        message={
          persistedMode === 'byok'
            ? 'This re-wraps your data keys under the validated customer key. Data is not re-encrypted; this typically completes in minutes.'
            : 'This switches custody to your KMS key and re-wraps your data keys under it. You become the holder of the root key and can revoke access at any time.'
        }
        confirmLabel="Confirm cut-over"
        cancelLabel="Cancel"
        tone="default"
        onConfirm={async () => {
          if (!effectiveArn) return
          try {
            await cutoverMutation.mutateAsync(effectiveArn)
          } catch (err) {
            throw new Error(
              errorMessage(
                err,
                'Cut-over failed and was rolled back. Your data remains under the current key.',
              ),
            )
          }
        }}
        onClose={() => setCutoverOpen(false)}
      />

      <ConfirmDialog
        open={switchToPlatformOpen}
        title="Switch to platform-managed keys?"
        message="This returns custody to the SecureEmbed-managed KMS key and re-wraps your data keys under it. Your data is not re-encrypted; this typically completes in minutes."
        confirmLabel="Confirm switch"
        cancelLabel="Cancel"
        tone="default"
        onConfirm={async () => {
          try {
            await switchToPlatformMutation.mutateAsync()
          } catch (err) {
            throw new Error(
              errorMessage(err, 'Could not switch to platform-managed keys. Please try again.'),
            )
          }
        }}
        onClose={() => setSwitchToPlatformOpen(false)}
      />

      <ConfirmDialog
        open={offboardOpen}
        title="Offboard & crypto-shred?"
        message="This runs export → retention-check → revocation, then permanently revokes the wrapping key and purges cached DEKs. This is irreversible — data not exported beforehand cannot be recovered."
        confirmLabel="Offboard & shred"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={async () => {
          try {
            await offboardMutation.mutateAsync()
          } catch (err) {
            throw new Error(errorMessage(err, 'Offboarding failed. Please try again.'))
          }
        }}
        onClose={() => setOffboardOpen(false)}
      />

      <ConfirmDialog
        open={revokeOpen}
        title="Revoke platform access to your key?"
        message="This retires the platform's grants on your key and deletes the search entries and derived vectors for projects attributed to this organisation. Search and analysis pause for your organisation. Backups holding earlier copies age out within 14 days. This cannot be undone."
        confirmLabel="Revoke and delete"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={async () => {
          try {
            await revokeMutation.mutateAsync()
          } catch (err) {
            throw new Error(errorMessage(err, 'Revocation failed. Please try again.'))
          }
        }}
        onClose={() => setRevokeOpen(false)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Custody mode radio card
// ---------------------------------------------------------------------------

function CustodyModeCard({
  title,
  badge,
  badgeClass,
  description,
  selected,
  onSelect,
}: {
  title: string
  badge: string
  badgeClass: string
  description: string
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`text-left rounded-xl border p-5 transition-colors ${
        selected
          ? 'border-iflow-500 bg-iflow-50 ring-1 ring-iflow-500'
          : 'border-gray-200 bg-white hover:border-iflow-300'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${
            selected ? 'border-iflow-500 bg-iflow-500' : 'border-gray-300 bg-white'
          }`}
        />
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-gray-800">{title}</span>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>
              {badge}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1.5">{description}</p>
        </div>
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Read-only key/value field
// ---------------------------------------------------------------------------

function ReadOnlyField({
  label,
  value,
  mono,
  compact,
}: {
  label: string
  value: string
  mono?: boolean
  compact?: boolean
}) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={`mt-1 text-gray-800 break-all ${compact ? 'text-xs' : 'text-sm'} ${
          mono ? 'font-mono' : 'font-medium'
        }`}
      >
        {value}
      </dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Key-policy code block (dark code surface is intentional — code only)
// ---------------------------------------------------------------------------

function KeyPolicyBlock({ statement }: { statement: unknown }) {
  const text = useMemo(() => prettyJson(statement), [statement])
  if (!statement) {
    return (
      <div className="rounded-lg border border-iflow-100 bg-iflow-50/60 px-4 py-3">
        <p className="text-sm text-iflow-900/70">Loading key-policy statement…</p>
      </div>
    )
  }
  return (
    <pre className="rounded-lg bg-slate-900 text-sky-200 text-xs font-mono px-4 py-3 overflow-x-auto">
      <code>{text}</code>
    </pre>
  )
}

// ---------------------------------------------------------------------------
// Copy-to-clipboard button
// ---------------------------------------------------------------------------

function CopyButton({
  label,
  text,
  disabled,
}: {
  label: string
  text: string
  disabled?: boolean
}) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!text) return
        void navigator.clipboard
          ?.writeText(text)
          .then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1800)
          })
          .catch(() => setCopied(false))
      }}
      className="inline-flex items-center gap-2 bg-white text-iflow-700 border border-iflow-200 px-4 py-2 rounded-lg text-sm font-medium hover:bg-iflow-50 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {copied ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
      {copied ? 'Copied' : label}
    </button>
  )
}
